import React, { useState } from 'react';

import { useNavigate } from 'react-router';

import { useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { Radio, RadioGroup } from '@cfa/react-core';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

import Constants, { routes } from 'constants/index';

import { Plan, PlanAssignment } from '@/_generated/api-clients/xp';
import { PathwayUser } from '@/_generated/api-clients/xp';
import { trainingModeAtom } from '@/atoms/trainingMode';

interface TrainTogetherProps {
  assignedTeamMembersList: { status?: PlanAssignment[] };
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
  planId: string;
  temporaryTeamMembers: PathwayUser[];
}

const setTrainingModeDataAtom = focusAtom(trainingModeAtom, o =>
  o.prop('trainingModeData'),
);

const TrainTogether: React.FC<TrainTogetherProps> = ({
  assignedTeamMembersList,
  isOpen,
  onClose,
  plan,
  planId,
  temporaryTeamMembers,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [languageSelected, setLanguageSelected] = useState('English');
  const [trainingModeTeamMembers, setTrainingModeTeamMembers] = useState([]);
  const setTrainingModeData = useSetAtom(setTrainingModeDataAtom);

  const onNextClick = () => {
    if (!languageSelected) return;
    sendNativeMessage({ type: 'trainingModeActive', payload: true });
    onClose();
    setTrainingModeData({
      estimatedMinutes: plan.estimatedTimeToCompleteInMinutes ?? 0,
      languageSelected,
      planId,
      planLabel: t(mapCategoryToAliasTranslation(plan.category)),
      planName: itemByLanguage(plan.name, ''),
      teamMembers: trainingModeTeamMembers,
      selectedTeamMembers: temporaryTeamMembers,
      assignedTeamMembersStatus: assignedTeamMembersList.status?.filter(
        teamMember => teamMember.status !== Constants.TRAINING_PLANS.COMPLETED,
      ),
    });
    navigate(
      {
        pathname: routes.plans.trainTogether({ planId }),
      },
      {
        state: { toTrainingMode: true },
      },
    );
  };

  const onBack = () => {
    setTrainingModeTeamMembers([]);
    onClose();
  };

  return (
    <>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.selectLanguage')}
        headerText={t('TrainingPlans.trainTogether.chooseLanguage')}
        isOpen={isOpen}
        onClose={onClose}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onNextClick}
        primaryButtonText={t('Button.next')}
        secondaryButtonHandler={onBack}
        secondaryButtonText={t('Button.back')}
      >
        <div className="flex cursor-pointer content-center">
          <RadioGroup
            defaultValue={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}
            orientation="vertical"
            onChange={value => setLanguageSelected(value)}
          >
            <Radio value={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}>
              {t('Language.english')}
            </Radio>
            <Radio value={Constants.LANGUAGE.SPANISH_LANGUAGE_CODE}>
              {t('Language.spanish')}
            </Radio>
          </RadioGroup>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default TrainTogether;
